/**
 * LoginScreen - Email input for passwordless login
 * Following new Figma design with light pink theme
 *
 * Simple wireframe UI - easy to replace later
 * User enters email → receives OTP code
 *
 * All styling is configurable in config/loginConfig.ts
 */

import { TermsSentence } from '@/components/legal/TermsSentence'
import { SUPPORT_EMAIL } from '@/config/brandConfig'
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { ErrorModal } from '@/components/ui/ErrorModal'
import {
  loginColors,
  loginText,
  loginLayout,
  loginButtons,
  loginFeatures
} from '@/config/loginConfig'
import { BirthdayBackground } from '@/components/ui/BirthdayBackground'
import { SeasonBackdrop } from '@/components/ui/SeasonBackdrop'
import { SEASON_LOOK, seasonPageBackground, entryLoginColors } from '@/config/seasonConfig'

// Google Identity Services types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            auto_select?: boolean
          }) => void
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: 'outline' | 'filled_blue' | 'filled_black'
              size?: 'large' | 'medium' | 'small'
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
              shape?: 'rectangular' | 'pill' | 'circle' | 'square'
              width?: number
            }
          ) => void
          prompt: () => void
        }
      }
    }
  }
}

interface LoginScreenProps {
  onCodeSent: (email: string) => void
}

export function LoginScreen({ onCodeSent }: LoginScreenProps) {
  const navigate = useNavigate()
  const { requestOTP, loginWithGoogle, error, clearError, isLoading } = useAuth()
  const [email, setEmail] = useState('')
  const [localError, setLocalError] = useState('')
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('')
  const [inviteToken, setInviteToken] = useState<string | null>(null)
  const [googleLoading, setGoogleLoading] = useState(false)

  // Google Sign-In configuration
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

  // Handle Google Sign-In callback
  const handleGoogleCallback = useCallback(async (response: { credential: string }) => {
    try {
      setGoogleLoading(true)
      setLocalError('')
      clearError()

      const result = await loginWithGoogle(response.credential)

      if (result.isApplicant) {
        // Applicant flow - redirect to apply page
        navigate('/apply')
      } else {
        // Check if user came from /vip and redirect back there
        const params = new URLSearchParams(window.location.search)
        const returnTo = params.get('returnTo')
        if (returnTo === '/vip' || returnTo === '/status') {
          navigate(returnTo, { replace: true })
        } else if (result.needsWelcome) {
          navigate('/welcome')
        } else {
          navigate('/')
        }
      }
    } catch (err) {
      console.error('Google Sign-In error:', err)
      setLocalError(err instanceof Error ? err.message : 'Google Sign-In failed')
    } finally {
      setGoogleLoading(false)
    }
  }, [loginWithGoogle, navigate, clearError])

  // Initialize Google Sign-In
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      return
    }

    // Wait for Google Identity Services script to load
    const initializeGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
        })

        // Render the Google Sign-In button
        const buttonContainer = document.getElementById('google-signin-button')
        if (buttonContainer) {
          window.google.accounts.id.renderButton(buttonContainer, {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            width: 280,
          })
        }
      }
    }

    // Check if script is already loaded
    if (window.google?.accounts?.id) {
      initializeGoogle()
    } else {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogle)
          initializeGoogle()
        }
      }, 100)

      // Cleanup
      return () => clearInterval(checkGoogle)
    }
  }, [GOOGLE_CLIENT_ID, handleGoogleCallback, showLoginForm])

  // Check for invite token in URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('invite')
    const inviteEmail = params.get('email')

    if (token) {
      setInviteToken(token)
      setShowLoginForm(true) // Auto-show login form for invited users

      // Pre-fill email if provided
      if (inviteEmail) {
        setEmail(decodeURIComponent(inviteEmail))
      }
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')
    clearError()

    // Validate email
    if (!email || !email.includes('@')) {
      setLocalError(loginText.emailValidationError)
      return
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

      // ---- INVITE PATH 1: If user has invite token from URL ----
      if (inviteToken) {
        const inviteResponse = await fetch(`${API_URL}/api/subscriptions/invites/${inviteToken}`)

        if (!inviteResponse.ok) {
          setErrorMessage('This invite link is invalid or has expired. Please contact support.')
          setShowErrorModal(true)
          return
        }

        const { invite } = await inviteResponse.json()

        // Verify email matches invite
        if (invite.email.toLowerCase() !== email.toLowerCase()) {
          setErrorMessage('Please use the email address this invite was sent to.')
          setLocalError('Email does not match invite')
          return
        }

        // Verify invite is still pending
        if (invite.status !== 'pending') {
          setErrorMessage('This invite has already been claimed or has expired.')
          setShowErrorModal(true)
          return
        }

        // Store invite token for later use in subscription creation
        localStorage.setItem('pending_invite_token', inviteToken)

        // BYPASS eligibility check and proceed directly to OTP
        await requestOTP(email)
        onCodeSent(email)
        return
      }

      // ---- STEP 1: Check if user already has a profile (returning user) ----
      try {
        const profileCheckResponse = await fetch(`${API_URL}/api/auth/check-profile?email=${encodeURIComponent(email)}`)

        if (profileCheckResponse.ok) {
          const profileCheckData = await profileCheckResponse.json()

          if (profileCheckData.hasProfile) {
            // User exists, proceed directly to OTP (normal login)
            await requestOTP(email)
            onCodeSent(email)
            return
          }
        }
      } catch (profileCheckError) {
        // no-op
      }

      // ---- STEP 2: Check if email has pending invite (new user with invite) ----
      try {
        // Check if this email has a pending invite
        const inviteCheckResponse = await fetch(`${API_URL}/api/subscriptions/invites/check-by-email?email=${encodeURIComponent(email)}`)

        if (inviteCheckResponse.ok) {
          const inviteCheckData = await inviteCheckResponse.json()

          if (inviteCheckData.hasInvite && inviteCheckData.invite) {
            // Store invite token for later use
            localStorage.setItem('pending_invite_token', inviteCheckData.invite.token)

            // BYPASS eligibility check and proceed directly to OTP
            await requestOTP(email)
            onCodeSent(email)
            return
          }
        }
      } catch (inviteCheckError) {
        // no-op
      }

      // ---- STEP 3: Send OTP ----
      // With the applicant system, ALL emails get OTP sent
      // - Existing profiles → normal login
      // - Pending invites → invite flow
      // - Stripe customers → normal login
      // - Unknown emails → applicant flow (new!)
      // The backend handles all cases and returns isApplicant flag if needed
      await requestOTP(email)
      // Move to code verification screen
      onCodeSent(email)
    } catch (err) {
      // Error is already set in context
      console.error('Failed to send code:', err)
    }
  }

  return (
    <>
      {/* Error Modal for non-existing emails */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Invite Not Found"
        message={errorMessage}
        buttonText="Try Again"
        secondaryAction={{
          label: "Apply for Access",
          onClick: () => {
            setShowErrorModal(false)
            navigate('/apply')
          }
        }}
      />

      <div className={`min-h-screen ${seasonPageBackground(loginColors.background)}`}>
        <SeasonBackdrop />
        <BirthdayBackground className="min-h-screen" enabled={false}>
          <div className={`flex items-start justify-center pt-20 ${loginLayout.padding}`}>
            <div className={`${loginLayout.maxWidth} w-full`}>
        {/* Header */}
        <div className={`text-center ${loginLayout.headerMarginBottom} ${loginFeatures.enableAnimations ? loginLayout.animationDuration : ''}`}>
          <h1 className={`text-xl font-bold ${loginColors.titleText}`}>
            {loginText.title}
          </h1>
          {loginFeatures.showSubtitle && (
            <p className={`mt-2 text-xs font-bold ${loginColors.subtitleText}`}>
              {loginText.subtitle}
            </p>
          )}
        </div>

        {/* Login Box */}
        <div className={`
          ${loginColors.containerBackground}
          ${loginColors.containerRounded}
          ${loginColors.containerShadow}
          ${loginLayout.containerPadding}
          ${loginLayout.buttonSpacing}
          ${loginFeatures.enableAnimations ? `${loginLayout.animationDuration} ${loginLayout.animationDelay}` : ''}
        `}>
          {!showLoginForm ? (
            // Initial state: Enter button
            <button
              onClick={() => setShowLoginForm(true)}
              className={`
                w-full
                ${loginLayout.buttonPadding}
                ${entryLoginColors.loginButtonBackground}
                ${entryLoginColors.loginButtonHover}
                ${entryLoginColors.loginButtonText}
                ${loginLayout.buttonRounded}
                ${loginButtons.login.fontWeight}
                ${loginLayout.buttonFontSize}
                ${loginFeatures.enableHoverEffects ? 'btn-hover' : ''}
              `}
            >
              {loginButtons.login.label}
            </button>
          ) : (
            // Login form after clicking "login"
            <form onSubmit={handleSubmit} className={`${loginLayout.formSpacing} ${loginFeatures.enableAnimations ? loginLayout.fadeInAnimation : ''}`}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {loginText.emailLabel}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={loginText.emailPlaceholder}
                  disabled={isLoading}
                  className={`
                    w-full px-3 py-2
                    border ${loginColors.inputBorder}
                    rounded-md shadow-sm
                    input-focus-ring
                    focus:outline-none
                    ${loginColors.inputFocus}
                  `}
                />
              </div>

              {/* Error messages */}
              {(localError || error) && (
                <div className={`${loginColors.errorText} text-sm ${loginFeatures.enableAnimations ? loginLayout.fadeInAnimation : ''}`}>
                  {localError || error}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading || googleLoading}
                className={`
                  w-full
                  ${loginLayout.buttonPadding}
                  ${entryLoginColors.submitButtonBackground}
                  ${entryLoginColors.submitButtonHover}
                  ${entryLoginColors.submitButtonText}
                  ${loginLayout.buttonRounded}
                  ${loginButtons.submit.fontWeight}
                  ${loginFeatures.enableHoverEffects ? 'btn-hover' : ''}
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                {isLoading ? loginButtons.submit.loadingLabel : loginButtons.submit.label}
              </button>

              {/* Google Sign-In divider and button */}
              {GOOGLE_CLIENT_ID && (
                <>
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">or</span>
                    </div>
                  </div>

                  {/* Google Sign-In Button */}
                  <div className="flex justify-center">
                    <div
                      id="google-signin-button"
                      className={googleLoading ? 'opacity-50 pointer-events-none' : ''}
                    ></div>
                  </div>

                  {googleLoading && (
                    <p className="text-center text-sm text-gray-500 mt-2">
                      Signing in with Google...
                    </p>
                  )}
                </>
              )}

              {/* Back button - at the bottom */}
              <button
                type="button"
                onClick={() => setShowLoginForm(false)}
                className={`
                  w-full py-2 mt-2
                  ${loginColors.backButtonText}
                  ${loginColors.backButtonHover}
                  text-sm
                  transition-smooth
                `}
              >
                {loginText.backButton}
              </button>
            </form>
          )}

          {/* Terms of service text */}
          {loginFeatures.showTerms && (
            <p className={`text-center text-xs ${loginColors.termsText} mt-4`}>
              <TermsSentence text={loginText.termsText} />
            </p>
          )}
        </div>

              {/* Support button */}
              <div className="text-center mt-6">
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full transition-all"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Support
                </a>
              </div>

              {/* Heart Image (heart look only - the pumpkin look shows the pumpkin behind the page) */}
              {SEASON_LOOK === 'heart' && (
              <div className="flex justify-center mt-2">
                <img
                  src="/heart.png"
                  alt="Heart"
                  className="w-[60vw] max-w-[400px] h-auto"
                  draggable={false}
                />
              </div>
              )}
            </div>
          </div>
        </BirthdayBackground>
      </div>
    </>
  )
}
